import { GeoLocation, VehicleInfo } from '../../types/api.types'
import { WizardVehicleConfiguration } from './WizardVehicleComponent'
import { WizardLocationConfiguration } from './WizardLocationComponent'
import { InfoBlockProps } from '../../components/InfoBlock/InfoBlock'

export type WizardStep = 'VEHICLE' | 'LOCATION' | 'OVERVIEW'
export const wizardStepOrder: WizardStep[] = ['VEHICLE', 'LOCATION', 'OVERVIEW']

export type WizardStepValidationWarning = WizardVehicleStepValidationWarning
type WizardVehicleStepValidationWarning = 'RS_NOT_BOOKED' | 'RS_NOT_ACTIVATED'

export interface WizardStepProps<T> {
  provider: string
  configuration: T
  setConfiguration: (configuration: T) => void
  setFailureState: (state: InfoBlockProps) => void
}

export interface WizardConfiguration {
  vehicle?: VehicleInfo
  location?: GeoLocation
  locationSuggestionByProvider?: GeoLocation
}

export function isFirstStep(step: WizardStep) {
  return step === wizardStepOrder[0]
}

export function isLastStep(step: WizardStep) {
  return step === wizardStepOrder[wizardStepOrder.length - 1]
}

export function getNextStep(step: WizardStep): WizardStep {
  if (isLastStep(step)) {
    return step
  }
  return wizardStepOrder[wizardStepOrder.indexOf(step) + 1]
}

export function getPreviousStep(step: WizardStep): WizardStep {
  if (isFirstStep(step)) {
    return step
  }
  return wizardStepOrder[wizardStepOrder.indexOf(step) - 1]
}

/**
 * Returns the maximum reachable step based on the current configuration and validation errors.
 *
 * If any step is invalid, subsequent steps cannot be reached.
 *
 * @param configuration The current wizard configuration
 * @param maxStep The maximum step that shall be reached
 * @param context The wizard context containing provider and vehicles
 * @returns The maximum reachable step based on the current configuration and validation errors.
 */
export function getMaxReachableStep(configuration: WizardConfiguration, maxStep: WizardStep): WizardStep {
  const maxStepIndex = wizardStepOrder.indexOf(maxStep)
  const firstInvalidStep = Array.from(Array(maxStepIndex).keys()).find((index) => {
    const step = wizardStepOrder[index]
    return !isWizardStepValid(step, configuration) || warningForWizardStep(step, configuration)
  })
  return wizardStepOrder[Math.min(firstInvalidStep ?? maxStepIndex, maxStepIndex)]
}

export function isWizardStepValid(step: WizardStep, configuration: WizardConfiguration) {
  switch (step) {
    case 'VEHICLE':
      return isWizardVehicleConfigurationValid(toWizardVehicleConfiguration(configuration))
    case 'LOCATION':
      return isWizardLocationConfigurationValid(toWizardLocationConfiguration(configuration))
    case 'OVERVIEW':
      return true
  }
}

export function warningForWizardStep(
  step: WizardStep,
  configuration: WizardConfiguration,
): WizardStepValidationWarning | undefined {
  switch (step) {
    case 'VEHICLE':
      return validationWarningForWizardVehicleConfiguration(toWizardVehicleConfiguration(configuration))
    default:
      return undefined
  }
}

// Vehicle Configuration

export function toWizardVehicleConfiguration(configuration: WizardConfiguration): WizardVehicleConfiguration {
  return { vehicle: configuration.vehicle }
}

export function updateWithVehicleConfiguration(
  configuration: WizardConfiguration,
  stepConfiguration: WizardVehicleConfiguration,
) {
  return { ...configuration, vehicle: stepConfiguration.vehicle }
}

function isWizardVehicleConfigurationValid(configuration: WizardVehicleConfiguration) {
  return configuration.vehicle != null
}

function validationWarningForWizardVehicleConfiguration(
  configuration: WizardVehicleConfiguration,
): WizardVehicleStepValidationWarning | undefined {
  const { vehicle } = configuration
  if (vehicle?.notSupportedReason === 'RS_NOT_BOOKED') {
    return 'RS_NOT_BOOKED'
  } else if (vehicle?.notSupportedReason === 'RS_NOT_ACTIVATED') {
    return 'RS_NOT_ACTIVATED'
  }
}

// Location

export function toWizardLocationConfiguration(configuration: WizardConfiguration): WizardLocationConfiguration {
  return {
    location: configuration.location,
    locationSuggestionByProvider: configuration.locationSuggestionByProvider,
  }
}

export function updateWithLocationConfiguration(
  configuration: WizardConfiguration,
  stepConfiguration: WizardLocationConfiguration,
) {
  return { ...configuration, location: stepConfiguration.location }
}

function isWizardLocationConfigurationValid(configuration: WizardLocationConfiguration) {
  return configuration.location != null || configuration.locationSuggestionByProvider != null
}
